/**
 * Stepper de activación (design-system §7 · Stepper): tres puntos de 8 px arriba de la pantalla.
 * Completado Coral 500, actual Coral 500 con anillo coral translúcido (`.fm-step-current`),
 * pendiente `--fm-border`. Sin números ni etiquetas visibles: la pantalla ya dice qué paso es;
 * la lista lleva `aria-label` "Paso n de N" y cada punto un texto solo para lectores de pantalla.
 */
import { t } from '../../i18n';

import { cn } from './cn';

export interface StepperProps {
  /** Paso actual, empezando en 1. */
  current: number;
  /** Total de pasos; por defecto 3. */
  steps?: number;
  className?: string;
}

type StepState = 'completed' | 'current' | 'pending';

function stateOf(index: number, current: number): StepState {
  if (index < current) {
    return 'completed';
  }
  return index === current ? 'current' : 'pending';
}

const STATE_CLASSES: Record<StepState, string> = {
  completed: 'bg-fm-primary',
  current: 'bg-fm-primary fm-step-current',
  pending: 'bg-fm-border',
};

export function Stepper({ current, steps = 3, className }: StepperProps) {
  const total = Math.max(1, steps);
  const clamped = Math.max(1, Math.min(current, total));

  return (
    <ol
      aria-label={t('common.stepper.label', { current: clamped, total })}
      className={cn('flex items-center justify-center gap-fm-3', className)}
    >
      {Array.from({ length: total }, (_, offset) => {
        const index = offset + 1;
        const state = stateOf(index, clamped);
        return (
          <li
            key={index}
            aria-current={state === 'current' ? 'step' : undefined}
            className={cn(
              'size-2 rounded-fm-pill transition-colors duration-fm-base ease-fm-standard motion-reduce:transition-none',
              STATE_CLASSES[state],
            )}
          >
            <span className="sr-only">{t(`common.stepper.${state}`, { index })}</span>
          </li>
        );
      })}
    </ol>
  );
}
